/**
 * boardgameCollectionAdapter — CollectionAdapter for the boardgame-tracker app.
 * Configures UnifiedCollectionView with boardgame data, filters, sorts, and renderers.
 * Supports multi-user view modes via projectUserFields / _userData.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Card, StarRating, InlineEditCell, formatDate, Pill } from '@code-burrow/design-system';
import { UnifiedCollectionView, useUserView, useUsers, usePod, useCurrentUser, projectUserFields, mergeUserData, filterPodItems, filterToUserItems, isCombinedView, ActivityFeed, ActivityService, CatalogCache } from '@code-burrow/tracker-core';
import BoardgameDetailModal from '../components/BoardgameDetailModal.jsx';

const BOARDGAME_USER_FIELDS = ['firstPlayed', 'rating', 'owned', 'wishlist', 'notes', 'playedWith'];

function getUserField(item, userName, fieldName) {
    return item._userData?.[userName]?.[fieldName] ?? null;
}

/**
 * Collect all unique playedWith names from raw items for filter options.
 */
function collectPlayedWithNames(rawItems, users) {
    const names = new Set();
    for (const item of rawItems) {
        for (const u of users) {
            const pw = getUserField(item, u.name, 'playedWith');
            if (Array.isArray(pw)) pw.forEach((n) => names.add(n));
        }
    }
    return [...names].sort();
}

/**
 * BoardgameCollectionView — wraps UnifiedCollectionView with a detail modal.
 */
export function BoardgameCollectionView({ backendService, pocketbaseURL, onItemsLoaded, externalSelect, pageMode = 'thisYear', onSearchOpen }) {
    const [selectedItem, setSelectedItem] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const { activeView } = useUserView();
    const { users } = useUsers();
    const { pod, allowedUsers, pods } = usePod();
    const currentUser = useCurrentUser();

    // All pod members' avatars are always shown on cards
    const visibleUserIds = useMemo(() => {
        const ids = new Set();
        for (const p of (pods || [])) {
            for (const m of (p.members || [])) {
                if (m.userId !== currentUser?.id) ids.add(m.userId);
            }
        }
        return ids;
    }, [pods, currentUser?.id]);

    useEffect(() => {
        if (externalSelect) setSelectedItem(externalSelect);
    }, [externalSelect]);

    const handleSavedOrDeleted = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    const allowedUserIds = useMemo(() => allowedUsers('boardgames'), [allowedUsers]);

    // Activity service for co-participant data + feed
    const activityServiceRef = useRef(null);
    if (!activityServiceRef.current && currentUser?.id) {
        activityServiceRef.current = new ActivityService(currentUser.id);
    }

    const adapter = useMemo(() =>
        boardgameCollectionAdapter(backendService, pocketbaseURL, setSelectedItem, refreshKey, activeView, pageMode, users, allowedUserIds, pod, onSearchOpen, currentUser, activityServiceRef.current, visibleUserIds, handleSavedOrDeleted),
        [backendService, pocketbaseURL, refreshKey, activeView, pageMode, users, allowedUserIds, pod, onSearchOpen, currentUser, visibleUserIds, handleSavedOrDeleted]
    );
    const feedAdapter = useMemo(() =>
        isCombinedView(activeView) && activityServiceRef.current && pod ? {
            ...adapter,
            feedRenderer: () => (
                <ActivityFeed
                    activityService={activityServiceRef.current}
                    podId={pod.id}
                    users={users}
                    onItemClick={(activity) => {
                        if (activity.itemType === 'boardgame') {
                            setSelectedItem({ id: activity.itemId, title: activity.itemTitle });
                        }
                    }}
                />
            ),
        } : adapter,
        [adapter, activeView, pod, users]
    );

    return (
        <>
            <UnifiedCollectionView collectionAdapter={feedAdapter} onItemsLoaded={onItemsLoaded} refreshKey={`${refreshKey}-${activeView}-${pageMode}-${users.length}`} />
            {selectedItem && (
                <BoardgameDetailModal
                    item={selectedItem}
                    backendService={backendService}
                    pocketbaseURL={pocketbaseURL}
                    onClose={() => setSelectedItem(null)}
                    onSaved={handleSavedOrDeleted}
                    onDeleted={handleSavedOrDeleted}
                />
            )}
        </>
    );
}

export function boardgameCollectionAdapter(backendService, pocketbaseURL, onItemClick = null, _refreshKey = 0, activeView = 'benny', pageMode = 'thisYear', users = [], allowedUserIds = null, pod = null, onSearchOpen = null, currentUser = null, activityService = null, visibleUserIds = null, onRefresh = null) {
    const currentYear = new Date().getFullYear();
    const isSingleUser = !isCombinedView(activeView);
    const user = isSingleUser ? activeView : null;

    const wasPlayedInYear = (rawItem, year) => {
        const check = (userName) => {
            const d = getUserField(rawItem, userName, 'firstPlayed');
            return d && new Date(d).getFullYear() === year;
        };
        if (user) return check(user);
        const userNames = users.length > 0 ? users.map((u) => u.name) : ['benny', 'gareth'];
        return userNames.some(check);
    };

    const hasPlayDate = (rawItem) => {
        const check = (userName) => !!getUserField(rawItem, userName, 'firstPlayed');
        if (user) return check(user);
        const userNames = users.length > 0 ? users.map((u) => u.name) : ['benny', 'gareth'];
        return userNames.some(check);
    };

    // We'll collect playedWith names dynamically during fetchItems
    let playedWithNames = [];

    const storageKeyMap = {
        thisYear: 'boardgame-tracker',
        library: 'boardgame-tracker-library',
    };

    const processBoardgameData = (collectionData, coParticipantMap, hasCoParticipantData = true) => {
        let rawBoardgames = collectionData.items;
        for (const item of rawBoardgames) {
            item._coParticipantIds = coParticipantMap[item.id] || [];
        }
        if (pageMode === 'pods') {
            rawBoardgames = filterPodItems(rawBoardgames, 'boardgames', [], users, 1);
        } else {
            rawBoardgames = filterToUserItems(rawBoardgames, activeView);
        }
        playedWithNames = collectPlayedWithNames(rawBoardgames, users);
        let items = rawBoardgames.map((bg) => projectUserFields(bg, 'boardgames', activeView, users));

        // ── Pods page: reconcile "together" data with explicit activities ──────
        // PodsPage uses collection overlap (both users have firstPlayed) to determine
        // "Played Together", but _coParticipantIds from the activities system is the
        // ground truth. We patch _userData so the design-system sees the correct state.
        if (pageMode === 'pods' && currentUser?.name && pod?.members?.length > 0) {
            const podMemberByUserId = Object.fromEntries(pod.members.map((m) => [m.userId, m]));
            const dismissedKey = `pods-dismissed-${currentUser.id}`;
            const dismissed = (() => {
                try { return new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]')); }
                catch { return new Set(); }
            })();
            for (let idx = 0; idx < items.length; idx++) {
                const item = items[idx];
                const coParticipantIds = new Set(item._coParticipantIds || []);
                const currentUserDate = item._userData?.[currentUser.name]?.firstPlayed;
                let newUserData = null;
                let isSyntheticForCurrentUser = false;

                // Part 1: Ensure all explicitly-together participants have a firstPlayed date.
                // A single user marking something "together" is sufficient — synthesize for
                // anyone who hasn't independently added the item yet (including the current user).
                if (coParticipantIds.size > 0) {
                    let referenceDate = currentUserDate;
                    if (!referenceDate) {
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (m) {
                                const d = item._userData?.[m.userName]?.firstPlayed;
                                if (d) { referenceDate = d; break; }
                            }
                        }
                    }
                    if (referenceDate) {
                        if (!currentUserDate && !dismissed.has(item.id)) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[currentUser.name] = { ...(item._userData?.[currentUser.name] || {}), firstPlayed: referenceDate };
                            isSyntheticForCurrentUser = true;
                        }
                        for (const memberId of coParticipantIds) {
                            const m = podMemberByUserId[memberId];
                            if (!m) continue;
                            const existing = (newUserData || item._userData)?.[m.userName];
                            if (!existing?.firstPlayed) {
                                if (!newUserData) newUserData = { ...item._userData };
                                newUserData[m.userName] = { ...(item._userData?.[m.userName] || {}), firstPlayed: referenceDate };
                            }
                        }
                    }
                }

                // Part 2: Clear firstPlayed for non-co-participants who have one independently
                // → prevents false "together" from plain collection overlap.
                // Skip when coMaps are empty (cached load) — would incorrectly strip all dates.
                const effectiveCurrentDate = (newUserData || item._userData)?.[currentUser.name]?.firstPlayed;
                if (hasCoParticipantData && effectiveCurrentDate) {
                    for (const [memberId, member] of Object.entries(podMemberByUserId)) {
                        if (memberId === currentUser.id) continue;
                        if (coParticipantIds.has(memberId)) continue;
                        const mData = (newUserData || item._userData)?.[member.userName];
                        if (mData?.firstPlayed) {
                            if (!newUserData) newUserData = { ...item._userData };
                            newUserData[member.userName] = { ...mData, firstPlayed: null };
                        }
                    }
                }

                if (newUserData) {
                    items[idx] = { ...item, _userData: newUserData, ...(isSyntheticForCurrentUser && { _syntheticForCurrentUser: true }) };
                }
            }
        }

        if (activeView === 'pod' && pageMode !== 'pods') {
            const podIds = new Set(filterPodItems(rawBoardgames, 'boardgames', [], users, pod?.togetherThreshold).map((b) => b.id));
            items = items.filter((item) => podIds.has(item.id));
        }
        if (pageMode === 'thisYear') {
            const activeIds = new Set(rawBoardgames.filter((b) => hasPlayDate(b)).map((b) => b.id));
            items = items.filter((item) => activeIds.has(item.id));
        }
        return items;
    };

    return {
        storageKey: storageKeyMap[pageMode] || 'boardgame-tracker',
        defaultSortField: 'firstPlayed',
        defaultSortDirection: 'desc',

        getTabValue: pageMode === 'thisYear'
            ? (item) => item.firstPlayed ? new Date(item.firstPlayed).getFullYear() : null
            : null,

        getImageUrl: (item) => {
            const pbImage = item.thumbImage;
            return pbImage && pocketbaseURL
                ? `${pocketbaseURL}/api/files/boardgames/${item.id}/${pbImage}`
                : item.imageURL ?? null;
        },

        // ── Data fetching ──────────────────────────────────────────────────────
        fetchItems: async () => {
            const catalogCache = new CatalogCache('boardgames');
            const fullCache = new CatalogCache(`boardgames:${currentUser?.id}`);

            const getCatalog = async () => {
                const c = await catalogCache.get();
                if (c?.items) return c.items;
                const data = await backendService.getCatalog();
                catalogCache.set(data.items).catch(() => {});
                return data.items;
            };

            const [catalogItems, userData, coParticipantMap] = await Promise.all([
                getCatalog(),
                backendService.getUserData(currentUser?.id),
                activityService ? activityService.getCoParticipantMap('boardgame') : Promise.resolve({}),
            ]);

            let items = [...catalogItems];
            const allowedIds = new Set((userData.users || []).map((u) => u.id));
            for (const [, jt] of Object.entries(userData.joinData || {})) {
                items = mergeUserData(items, jt.records, jt.itemIdField, jt.userFields, userData.users, allowedIds);
            }
            const collectionData = { items, users: userData.users };
            fullCache.set(collectionData).catch(() => {});
            return processBoardgameData(collectionData, coParticipantMap);
        },

        getCachedItems: async () => {
            const fullCache = new CatalogCache(`boardgames:${currentUser?.id}`);
            const cached = await fullCache.getEvenIfStale();
            if (!cached?.items) return null;
            try {
                const items = processBoardgameData(cached.items, {}, false);
                return { items, isStale: cached.isStale, timestamp: cached.timestamp };
            } catch { return null; }
        },

        // ── Context menu ──────────────────────────────────────────────────────
        backendService,
        activeView,
        getContextMenuActions: (item) => [
            {
                label: 'Refresh Metadata',
                onClick: async () => {
                    const result = await backendService.refreshBoardgame(item.id);
                    if (result && !result.success) throw new Error(result.error);
                },
            },
            {
                label: 'Delete',
                variant: 'danger',
                onClick: async () => {
                    try {
                        const joins = await backendService.db.pb.collection('user_boardgames').getFullList({
                            filter: `boardgame="${item.id}"`, $autoCancel: false,
                        });
                        for (const j of joins) await backendService.proxy.collection('user_boardgames').delete(j.id);
                    } catch {}
                    await backendService.proxy.collection('boardgames').delete(item.id);
                },
            },
        ],

        // ── Filter fields ──────────────────────────────────────────────────────
        filterFields: [
            {
                key: 'playStyle',
                label: 'Play Style',
                type: 'multiselect',
                allLabel: 'All Play Styles',
                targetFields: ['playStyle'],
                staticOptions: [
                    { value: 'Co-op', label: 'Co-op' },
                    { value: 'Competitive', label: 'Competitive' },
                    { value: 'One vs All', label: 'One vs All' },
                ],
            },
            {
                key: 'owned',
                label: 'Owned',
                type: 'toggle',
                targetFields: [],
                filterFn: (item, value) => {
                    if (value !== 'true') return true;
                    if (item._userData) {
                        // In single-user view: only check that user's owned flag.
                        // In combined views: show if any pod member owns it.
                        if (!isCombinedView(activeView)) {
                            return item._userData[activeView]?.owned === true;
                        }
                        return Object.values(item._userData).some((ud) => ud.owned === true);
                    }
                    return item.owned === true;
                },
            },
            {
                key: 'playedWith',
                label: 'Played With',
                type: 'select',
                targetFields: [],
                getOptions: () => playedWithNames.map((n) => ({ value: n, label: n })),
                filterFn: (item, value) => {
                    if (item._userData) {
                        // In single-user view: only check that user's playedWith list.
                        // In combined views: show if any pod member played with this person.
                        if (!isCombinedView(activeView)) {
                            const pw = item._userData[activeView]?.playedWith;
                            return Array.isArray(pw) && pw.includes(value);
                        }
                        return Object.values(item._userData).some(
                            (ud) => Array.isArray(ud.playedWith) && ud.playedWith.includes(value)
                        );
                    }
                    return Array.isArray(item.playedWith) && item.playedWith.includes(value);
                },
            },
        ],

        // ── Sort fields ────────────────────────────────────────────────────────
        sortFields: [
            {
                key: 'title',
                label: 'Title',
                getValue: (item) => (item.title ?? '').toLowerCase(),
            },
            {
                key: 'firstPlayed',
                label: 'First Played',
                getValue: (item) => item.firstPlayed ? new Date(item.firstPlayed).getTime() : null,
            },
            {
                key: 'rating',
                label: 'Rating',
                getValue: (item) => item.rating ?? null,
            },
            {
                key: 'yearPublished',
                label: 'Year Published',
                getValue: (item) => item.yearPublished ?? null,
            },
            {
                key: 'players',
                label: 'Players',
                getValue: (item) => item.maxPlayers ?? null,
            },
            {
                key: 'playingTime',
                label: 'Play Time',
                getValue: (item) => item.playingTime ?? null,
            },
            {
                key: 'bggRating',
                label: 'BGG Rating',
                getValue: (item) => item.bggRating ?? null,
            },
        ],

        // ── Grid item renderer ─────────────────────────────────────────────────
        renderGridItem: (item) => {
            const pbImage = item.thumbImage;
            const imageURL = pbImage && pocketbaseURL
                ? `${pocketbaseURL}/api/files/boardgames/${item.id}/${pbImage}`
                : item.imageURL ?? null;

            // Player count display
            const playerStr = item.minPlayers && item.maxPlayers
                ? item.minPlayers === item.maxPlayers
                    ? `${item.minPlayers}P`
                    : `${item.minPlayers}-${item.maxPlayers}P`
                : null;

            // Play time display
            const timeStr = item.playingTime ? `${item.playingTime}m` : null;

            // Collect playedWith tags from user data
            const playedWithTags = new Set();
            if (item._userData) {
                for (const ud of Object.values(item._userData)) {
                    if (Array.isArray(ud.playedWith)) ud.playedWith.forEach((n) => playedWithTags.add(n));
                }
            } else if (Array.isArray(item.playedWith)) {
                item.playedWith.forEach((n) => playedWithTags.add(n));
            }

            // Date display
            let dateStr;
            if (isCombinedView(activeView)) {
                const userDates = (users.length > 0 ? users : []).map((u) => ({
                    name: u.name,
                    emoji: u.emoji,
                    firstPlayed: getUserField(item, u.name, 'firstPlayed') || null,
                })).filter((ud) => ud.firstPlayed);

                const allSame = userDates.length > 1 &&
                    userDates.every((ud) => formatDate(ud.firstPlayed) === formatDate(userDates[0].firstPlayed));

                if (userDates.length > 0) {
                    if (allSame) {
                        dateStr = formatDate(userDates[0].firstPlayed);
                    } else {
                        dateStr = userDates.map((ud) => `${ud.emoji} ${formatDate(ud.firstPlayed)}`).join('  ');
                    }
                }
            } else {
                dateStr = item.firstPlayed ? formatDate(item.firstPlayed) : null;
            }

            // Build statusPill content: tags (inline with avatars)
            const statusContent = playedWithTags.size > 0 ? (
                <div className="flex flex-wrap gap-1">
                    {[...playedWithTags].map((tag) => (
                        <Pill key={tag} className="bg-accent-primary/10 text-cyan-300">{tag}</Pill>
                    ))}
                </div>
            ) : null;

            // Build statusMeta: date + player/time stats (below avatar row, full width)
            let statusMetaContent = (dateStr || playerStr || timeStr) ? (
                <>
                    {dateStr && (
                        <div>
                            <span className="text-xs text-text-tertiary">{dateStr}</span>
                        </div>
                    )}
                    {(playerStr || timeStr) && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-text-tertiary">
                            {playerStr && (
                                <span className="flex items-center gap-0.5">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                                    </svg>
                                    {playerStr}
                                </span>
                            )}
                            {timeStr && (
                                <span className="flex items-center gap-0.5">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {timeStr}
                                </span>
                            )}
                        </div>
                    )}
                </>
            ) : null;

            // Pods page: "Add to my list" / "Dismiss" for items a co-participant tagged together
            // but the current user hasn't independently tracked yet.
            if (pageMode === 'pods' && item._syntheticForCurrentUser && onRefresh) {
                const syntheticDate = item._userData?.[currentUser.name]?.firstPlayed;
                const dismissedKey = `pods-dismissed-${currentUser.id}`;
                const actionButtons = (
                    <div className="flex gap-1.5 mt-2">
                        <button
                            onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                    await backendService.upsertUserBoardgame(currentUser.name, item.id, { firstPlayed: syntheticDate || null });
                                    onRefresh();
                                } catch {}
                            }}
                            className="text-xs px-2 py-0.5 rounded bg-accent-primary/20 text-accent-primary hover:bg-accent-primary/30 transition-colors"
                        >
                            Add to my list
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                try {
                                    const set = new Set(JSON.parse(localStorage.getItem(dismissedKey) || '[]'));
                                    set.add(item.id);
                                    localStorage.setItem(dismissedKey, JSON.stringify([...set]));
                                } catch {}
                                onRefresh();
                            }}
                            className="text-xs px-2 py-0.5 rounded bg-surface-secondary text-text-tertiary hover:text-text-primary transition-colors"
                        >
                            Dismiss
                        </button>
                    </div>
                );
                statusMetaContent = statusMetaContent ? <>{statusMetaContent}{actionButtons}</> : actionButtons;
            }

            // Two-tier avatar system
            const coPartIds = item._coParticipantIds || [];
            const coPartIdSet = new Set(coPartIds);
            const myAvatar = currentUser ? { emoji: users.find(u => u.name === currentUser.name)?.emoji, name: currentUser.name } : null;

            let coParticipants = undefined;
            if (coPartIds.length > 0 && myAvatar?.emoji) {
                const visibleCoUsers = users.filter(u => coPartIdSet.has(u.id) && (!visibleUserIds || visibleUserIds.has(u.id)));
                if (visibleCoUsers.length > 0) {
                    coParticipants = [
                        myAvatar,
                        ...visibleCoUsers.map(u => ({ emoji: u.emoji, name: u.displayName || u.name })),
                    ];
                }
            }

            return (
                <Card
                    key={item.id}
                    imageUrl={imageURL}
                    imageAlt={item.title}
                    title={item.title}
                    subtitle={item.yearPublished ? `(${item.yearPublished})` : undefined}
                    statusPill={statusContent}
                    statusMeta={statusMetaContent}
                    coParticipants={coParticipants}
                    onClick={onItemClick ? () => onItemClick({ ...item, imageURL }) : undefined}
                />
            );
        },

        // ── Table columns ──────────────────────────────────────────────────────
        renderTableColumns: buildBoardgameTableColumns(backendService, pocketbaseURL, onItemClick, activeView, users),

        // ── Bulk edit ────────────────────────────────────────────────────────
        getItemId: (item) => item.bggId || item.id,
        bulkEditFields: isCombinedView(activeView) ? [] : [
            { key: 'rating', label: 'Rating', type: 'rating' },
            { key: 'togetherWith', label: 'Together', type: 'podMembers' },
        ],
        onBulkSave: async (selectedItems, updates) => {
            const errors = [];
            const { togetherWith, ...userUpdates } = updates;
            const hasUserUpdates = Object.keys(userUpdates).length > 0;

            for (const item of selectedItems) {
                try {
                    if (hasUserUpdates) {
                        await backendService.upsertUserBoardgame(activeView, item.id, userUpdates);
                    }
                    if (togetherWith && togetherWith.length > 0 && activityService && pod && currentUser) {
                        const memberIds = (pod.members || [])
                            .filter(m => togetherWith.includes(m.userId))
                            .map(m => m.userId);
                        if (memberIds.length > 0) {
                            await activityService.create({
                                type: 'played',
                                itemType: 'boardgame',
                                itemId: item.id,
                                itemTitle: item.title || '',
                                participants: [currentUser.id, ...memberIds],
                                pod: pod.id,
                                date: new Date().toISOString(),
                            });
                        }
                    }
                } catch (err) {
                    errors.push({ title: item.title, error: err.message });
                }
            }
            if (errors.length > 0) throw new Error(`Failed to update ${errors.length} item(s)`);
        },

        // ── Empty state ────────────────────────────────────────────────────────
        emptyState: (() => {
            const isOtherUser = !isCombinedView(activeView) && activeView !== currentUser?.name;
            const viewUser = isOtherUser ? users.find((u) => u.name === activeView) : null;
            const searchBtn = onSearchOpen ? (
                <button onClick={onSearchOpen} className="px-4 py-2 rounded-md bg-accent-primary text-text-inverse text-sm font-medium hover:brightness-90 transition-all">
                    Search & Add
                </button>
            ) : undefined;

            if (isOtherUser) return {
                title: `No board games shared by ${viewUser?.displayName || activeView} yet`,
            };
            if (activeView === 'pod') return {
                title: 'No games played together yet',
                description: 'Games your pod has played will appear here',
            };
            if (pageMode === 'thisYear') return {
                title: 'No games played yet',
                description: 'Games you play will show up here',
                action: searchBtn,
            };
            return {
                title: 'No board games yet',
                description: !pod
                    ? 'Search to add your first board game. You can also create a Pod in Settings to share with friends.'
                    : 'Get started by adding a board game to your collection',
                action: searchBtn,
            };
        })(),

        actions: [],
    };
}

// ── Table column builder ───────────────────────────────────────────────────────

function buildBoardgameTableColumns(backendService, pocketbaseURL, onItemClick, activeView, users) {
    const handleSave = async (item, field, value) => {
        await backendService.upsertUserBoardgame(activeView, item.id, { [field]: value });
    };

    return [
        {
            key: 'image',
            header: '',
            width: '50px',
            renderCell: (item) => {
                const pbImage = item.thumbImage;
                const url = pbImage && pocketbaseURL
                    ? `${pocketbaseURL}/api/files/boardgames/${item.id}/${pbImage}`
                    : item.imageURL ?? null;
                return url ? (
                    <img src={url} alt={item.title} className="h-12 w-12 object-cover rounded-sm" loading="lazy" />
                ) : (
                    <div className="h-12 w-12 bg-bg-tertiary rounded-sm flex items-center justify-center">
                        <span className="text-lg">🎲</span>
                    </div>
                );
            },
        },
        {
            key: 'title',
            header: 'Title',
            sortable: 'title',
            renderCell: (item) => {
                const content = (
                    <div className="flex flex-col">
                        <span className="font-medium text-text-primary">{item.title}</span>
                        {item.yearPublished && <span className="text-xs text-text-tertiary">{item.yearPublished}</span>}
                    </div>
                );
                return onItemClick ? (
                    <button onClick={() => onItemClick(item)} className="hover:text-accent-primary transition-colors duration-150 text-left">
                        {content}
                    </button>
                ) : content;
            },
        },
        {
            key: 'playStyle',
            header: 'Style',
            width: '90px',
            renderCell: (item) => (
                <span className="text-text-secondary text-xs">{item.playStyle || '-'}</span>
            ),
        },
        {
            key: 'players',
            header: 'Players',
            sortable: 'players',
            width: '90px',
            renderCell: (item) => (
                <span className="text-text-secondary">
                    {item.minPlayers && item.maxPlayers
                        ? item.minPlayers === item.maxPlayers
                            ? `${item.minPlayers}`
                            : `${item.minPlayers}-${item.maxPlayers}`
                        : '-'}
                </span>
            ),
        },
        {
            key: 'playingTime',
            header: 'Time',
            sortable: 'playingTime',
            width: '80px',
            renderCell: (item) => (
                <span className="text-text-secondary">{item.playingTime ? `${item.playingTime}m` : '-'}</span>
            ),
        },
        {
            key: 'firstPlayed',
            header: 'First Played',
            sortable: 'firstPlayed',
            width: '130px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="firstPlayed"
                    editType="date"
                    displayValue={formatDate(item.firstPlayed)}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'rating',
            header: 'Rating',
            sortable: 'rating',
            width: '120px',
            renderCell: (item, { onItemUpdated } = {}) => (
                <InlineEditCell
                    item={item}
                    field="rating"
                    editType="rating"
                    displayValue={<StarRating rating={item.rating} />}
                    onSave={async (it, field, value) => { await handleSave(it, field, value); onItemUpdated?.(it, field, value); }}
                    readOnly={isCombinedView(activeView)}
                />
            ),
        },
        {
            key: 'bggRating',
            header: 'BGG',
            sortable: 'bggRating',
            width: '60px',
            renderCell: (item) => (
                <span className="text-text-secondary">{item.bggRating ? item.bggRating.toFixed(1) : '-'}</span>
            ),
        },
    ];
}
